import { create } from "zustand";
import { AttributionTypeChoices } from "@/__generated__/schema.graphql.types";
import { MembershipsQuery } from "@/graphql/queries/memberships.graphql.types";
import { ClientForVisitCreation } from "@/hooks/visits/useCreateVisit";
import { AttributionEmployee } from "@/types/attributions";

export enum CreateClientMembershipStep {
  CLIENT_SELECTION,
  MEMBERSHIP_SELECTION,
  SUMMARY,
  SUCCESS,
}

type StoreState = {
  step: CreateClientMembershipStep;
  selectedClient: ClientForVisitCreation;
  selectedMembership: MembershipsQuery["membership"][number];
  shouldRemoveInitiationFee: boolean;
  consentToCardCharges: boolean;
  consentToCardChargesError: boolean;
  startDate: Date;
  consentToTerms: boolean;
  signatureUrl: string;
  consentToTermsError: boolean;
  noDefaultCardError: boolean;
  isMembershipSaleDrawerOpen: boolean;
  attributionType: AttributionTypeChoices | null;
  attributionEmployee: AttributionEmployee | null;
  setStep: (step: CreateClientMembershipStep) => void;
  setSelectedClient: (client: ClientForVisitCreation) => void;
  setSelectedMembership: (
    membership: MembershipsQuery["membership"][number]
  ) => void;
  setShouldRemoveInitiationFee: (remove: boolean) => void;
  setConsentToCardCharges: (consent: boolean) => void;
  setConsentToCardChargesError: (error: boolean) => void;
  setStartDate: (startDate: Date) => void;
  setConsentToTerms: (consent: boolean) => void;
  setSignatureUrl: (url: string) => void;
  setConsentToTermsError: (error: boolean) => void;
  setNoDefaultCardError: (error: boolean) => void;
  setIsMembershipSaleDrawerOpen: (isDrawerOpen: boolean) => void;
  setAttributionType: (attributionType: AttributionTypeChoices) => void;
  setAttributionEmployee: (attributionEmployee: AttributionEmployee) => void;
  reset: () => void;
};

const INITIAL_STATE = {
  step: CreateClientMembershipStep.CLIENT_SELECTION,
  selectedClient: null,
  selectedMembership: null,
  shouldRemoveInitiationFee: false,
  consentToCardCharges: false,
  startDate: new Date(),
  consentToTerms: false,
  signatureUrl: "",
  isMembershipSaleDrawerOpen: false,
  consentToCardChargesError: false,
  consentToTermsError: false,
  noDefaultCardError: false,
  attributionType: null,
  attributionEmployee: null,
} as const;

const useCreateClientMembershipStore = create<StoreState>((set) => ({
  ...INITIAL_STATE,
  // Setters
  setStep: (step) => set(() => ({ step })),
  setSelectedClient: (selectedClient) => set(() => ({ selectedClient })),
  setSelectedMembership: (selectedMembership) =>
    set(() => ({ selectedMembership })),
  setShouldRemoveInitiationFee: (shouldRemoveInitiationFee) =>
    set(() => ({ shouldRemoveInitiationFee })),
  setConsentToCardCharges: (consentToCardCharges) =>
    set(() => ({ consentToCardCharges })),
  setConsentToCardChargesError: (consentToCardChargesError) =>
    set(() => ({ consentToCardChargesError })),
  setStartDate: (startDate) => set(() => ({ startDate })),
  setConsentToTerms: (consentToTerms) => set(() => ({ consentToTerms })),
  setSignatureUrl: (signatureUrl) => set(() => ({ signatureUrl })),
  setConsentToTermsError: (consentToTermsError) =>
    set(() => ({ consentToTermsError })),
  setNoDefaultCardError: (noDefaultCardError) =>
    set(() => ({ noDefaultCardError })),
  setIsMembershipSaleDrawerOpen: (isMembershipSaleDrawerOpen) =>
    set(() => ({ isMembershipSaleDrawerOpen })),
  setAttributionType: (attributionType) => set(() => ({ attributionType })),
  setAttributionEmployee: (attributionEmployee) =>
    set(() => ({ attributionEmployee })),
  reset: () => set(() => ({ ...INITIAL_STATE })),
}));

export default useCreateClientMembershipStore;

export const getStep = (state: StoreState) => state.step;
export const getSelectedClient = (state: StoreState) => state.selectedClient;
export const getSelectedMembership = (state: StoreState) =>
  state.selectedMembership;
export const getShouldRemoveInitiationFee = (state: StoreState) =>
  state.shouldRemoveInitiationFee;
export const getStartDate = (state: StoreState) => state.startDate;
export const getConsentToCardCharges = (state: StoreState) =>
  state.consentToCardCharges;
export const getConsentToTerms = (state: StoreState) => state.consentToTerms;
export const getSignatureUrl = (state: StoreState) => state.signatureUrl;
export const getIsMembershipSaleDrawerOpen = (state: StoreState) =>
  state.isMembershipSaleDrawerOpen;
export const getSetStep = (state: StoreState) => state.setStep;
export const getSetSelectedClient = (state: StoreState) =>
  state.setSelectedClient;
export const getSetSelectedMembership = (state: StoreState) =>
  state.setSelectedMembership;
export const getSetShouldRemoveInitiationFee = (state: StoreState) =>
  state.setShouldRemoveInitiationFee;
export const getSetConsentToCardCharges = (state: StoreState) =>
  state.setConsentToCardCharges;
export const getSetStartDate = (state: StoreState) => state.setStartDate;
export const getSetConsentToTerms = (state: StoreState) =>
  state.setConsentToTerms;
export const getSetSignatureUrl = (state: StoreState) => state.setSignatureUrl;
export const getSetIsMembershipSaleDrawerOpen = (state: StoreState) =>
  state.setIsMembershipSaleDrawerOpen;
export const getReset = (state: StoreState) => state.reset;
export const getConsentToCardChargesError = (state: StoreState) =>
  state.consentToCardChargesError;
export const getConsentToTermsError = (state: StoreState) =>
  state.consentToTermsError;
export const getSetConsentToCardChargesError = (state: StoreState) =>
  state.setConsentToCardChargesError;
export const getSetConsentToTermsError = (state: StoreState) =>
  state.setConsentToTermsError;
export const getNoDefaultCardError = (state: StoreState) =>
  state.noDefaultCardError;
export const getSetNoDefaultCardError = (state: StoreState) =>
  state.setNoDefaultCardError;
export const getAttributionType = (state: StoreState) => state.attributionType;
export const getAttributionEmployee = (state: StoreState) =>
  state.attributionEmployee;
export const getSetAttributionType = (state: StoreState) =>
  state.setAttributionType;
export const getSetAttributionEmployee = (state: StoreState) =>
  state.setAttributionEmployee;
